/**
 * @fileoverview Branch Analytics hub page.
 * @description Two clickable cards linking to this group's sub-pages (Branch-wise Income Share,
 * Spread/C-I Ratio & Income Mix). Ported from the InsightBox Banking design system's own
 * `BranchAnalytics/Grid.tsx`; see `BankingMembershipPage.tsx` for the shared local
 * `fy`/`branchId` filter-state convention.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useCurrentSession } from '@/features/auth'
import {
  LATEST_FY,
  branchIncome,
  core,
  formatSignedPct,
  incomeExpenseMix,
  resolveBankingAccessScope,
  resolveFy,
  trendLabel,
  useBankingData,
  yoyGrowth,
} from '@/features/banking'
import PageHeader from '@/shared/components/molecules/PageHeader'
import ViewGridCard from '@/shared/components/molecules/ViewGridCard'
import { ROUTES } from '@/shared/constants/routes.constants'

import type { FyFilter } from '@/features/banking'

/**
 * @component
 */
function BankingBranchAnalyticsPage() {
  const { t } = useTranslation()
  const dataQuery = useBankingData()
  const [fyFilter] = useState<FyFilter>(LATEST_FY)
  const bankingScope = resolveBankingAccessScope(useCurrentSession().data?.bankingScope)
  const [branchId] = useState(bankingScope.defaultBranchId)

  const header = (
    <PageHeader
      kicker={t('banking.branchAnalytics.kicker')}
      title={t('nav.branchAnalytics')}
      description={t('banking.branchAnalytics.description')}
    />
  )

  if (dataQuery.isPending) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <div className="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-5">
          <ViewGridCard
            to={ROUTES.BANKING_BRANCH_ANALYTICS_INCOME_SHARE}
            title=""
            description=""
            metricLabel=""
            metricValue=""
            trend={{ direction: 'flat', text: '' }}
            loading
          />
          <ViewGridCard
            to={ROUTES.BANKING_BRANCH_ANALYTICS_SPREAD_CI_INCOME_MIX}
            title=""
            description=""
            metricLabel=""
            metricValue=""
            trend={{ direction: 'flat', text: '' }}
            loading
          />
        </div>
      </div>
    )
  }

  if (dataQuery.isError) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <p className="text-sm text-danger">{t('banking.loadError')}</p>
      </div>
    )
  }

  const data = dataQuery.data
  const fy = resolveFy(fyFilter)
  const income = branchIncome(data, fy)[0]!
  const incomeGrowth = yoyGrowth(fy, (f) => core(data, f, branchId).totalIncome)
  const mix = incomeExpenseMix(data, fy, branchId)

  return (
    <div className="flex flex-col gap-4">
      {header}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(320px,100%),1fr))] gap-5">
        <ViewGridCard
          to={ROUTES.BANKING_BRANCH_ANALYTICS_INCOME_SHARE}
          title={t('banking.branchAnalytics.incomeShare.title')}
          description={t('banking.branchAnalytics.incomeShare.description')}
          metricLabel={t('banking.branchAnalytics.incomeShare.metricLabel', {
            branch: income.branch.shortName,
          })}
          metricValue={`${income.sharePct.toFixed(2)}%`}
          trend={{
            direction: trendLabel(incomeGrowth),
            text:
              incomeGrowth !== null
                ? t('banking.branchAnalytics.incomeYoyTrend', {
                    value: formatSignedPct(incomeGrowth),
                  })
                : t('banking.baseYear'),
          }}
        />
        <ViewGridCard
          to={ROUTES.BANKING_BRANCH_ANALYTICS_SPREAD_CI_INCOME_MIX}
          title={t('banking.branchAnalytics.spreadCiIncomeMix.title')}
          description={t('banking.branchAnalytics.spreadCiIncomeMix.description')}
          metricLabel={t('banking.branchAnalytics.spreadCiIncomeMix.metricLabel')}
          metricValue={`${mix.ciRatio.toFixed(2)}%`}
          trend={{
            direction: 'flat',
            text: t('banking.branchAnalytics.spreadTrend', { value: mix.spreadPct.toFixed(2) }),
          }}
        />
      </div>
    </div>
  )
}

export default BankingBranchAnalyticsPage
